import { ref, computed } from "vue";
import { f7 } from "framework7-vue";
import { useAccountStore } from "../pinia-stores/account.js";
import { useAuthStore } from "../pinia-stores/auth.js";
import { useMessageStore } from "../pinia-stores/message.js";

const MEMBERS_PANEL = ".createGroupLeftPanel";
const DETAILS_PANEL = ".saveGroupLeftPanel";
const CONVERSATION_PANEL = ".sendMessageLeftPanel";

/**
 * Group a new sub-group is added to. `null` means a brand new group.
 *
 * This is module state on purpose: the creation panel is mounted once, globally,
 * by `app.vue`, while the entry points live in the chat list, the new-message
 * panel and the group context menu. Every entry point used to render its own
 * copy of the panel and pass the parent group down as a prop, so
 * `f7.panel.open(".createGroupLeftPanel")` opened whichever copy came first in
 * the DOM and the panel that did open could be in the wrong mode.
 */
const parentGroup = ref(null);
const subGroupParticipants = ref([]);
const isLoadingParticipants = ref(false);

export function useGroupCreation() {
  const accountStore = useAccountStore();
  const authStore = useAuthStore();
  const messageStore = useMessageStore();

  const isSubGroupCreation = computed(() => !!parentGroup.value?.id);
  const parentGroupName = computed(() => parentGroup.value?.name || "");
  const isLoadingCandidates = computed(() =>
    isSubGroupCreation.value
      ? isLoadingParticipants.value
      : !accountStore.users?.length
  );

  /** People that can be added to the group being created. */
  const candidates = computed(() => {
    const source = isSubGroupCreation.value
      ? subGroupParticipants.value
      : accountStore.users;
    const currentUserId = authStore.profile?.sub;
    return (source || []).filter(
      (user) => !currentUserId || user.userId !== currentUserId
    );
  });

  async function loadCandidates() {
    if (!isSubGroupCreation.value) {
      if (!accountStore.users?.length) accountStore.getAllUsers();
      return;
    }
    isLoadingParticipants.value = true;
    try {
      subGroupParticipants.value =
        (await messageStore.getGroupParticipantList(parentGroup.value.id)) || [];
    } finally {
      isLoadingParticipants.value = false;
    }
  }

  /**
   * Starts the two step flow: members first, name and avatar second.
   *
   * @param {Object|null} group Existing group the new sub-group belongs to.
   */
  function openGroupCreation(group = null) {
    parentGroup.value = group?.id ? group : null;
    subGroupParticipants.value = [];
    loadCandidates();
    openMembersPanel();
  }

  function openMembersPanel() {
    const conversationPanel = f7.panel.get(CONVERSATION_PANEL);
    if (conversationPanel && conversationPanel.opened) {
      conversationPanel.once("panel:closed", () => f7.panel.open(MEMBERS_PANEL));
      conversationPanel.close();
      return;
    }
    f7.panel.open(MEMBERS_PANEL);
  }

  function openDetailsPanel() {
    f7.panel.open(DETAILS_PANEL);
  }

  /** Closes both steps and forgets the group a sub-group was pending for. */
  function closeGroupCreation() {
    f7.panel.close(DETAILS_PANEL);
    f7.panel.close(MEMBERS_PANEL);
    resetGroupCreation();
  }

  function resetGroupCreation() {
    parentGroup.value = null;
    subGroupParticipants.value = [];
  }

  return {
    parentGroup,
    isSubGroupCreation,
    parentGroupName,
    candidates,
    isLoadingCandidates,
    loadCandidates,
    openGroupCreation,
    openMembersPanel,
    openDetailsPanel,
    closeGroupCreation,
    resetGroupCreation,
  };
}
